// Billing-level state machine + money guards.

import { InvalidTransitionError, RuleViolationError } from '../errors.js';
import type { Paise } from '../money/index.js';
import { paise } from '../money/index.js';

export type DocumentStatus = 'draft' | 'issued' | 'partially_paid' | 'paid' | 'written_off';

/** Rule 3: only Draft is mutable. Post-issue changes require credit-note or explicit state moves. */
export function assertDocumentMutable(status: DocumentStatus): void {
  if (status !== 'draft') {
    throw new RuleViolationError(
      'billing.immutable',
      `Document status is "${status}". Issued documents are append-only; use a credit note.`,
      { status },
    );
  }
}

const VALID: Record<DocumentStatus, readonly DocumentStatus[]> = {
  draft: ['issued'],
  issued: ['partially_paid', 'paid', 'written_off'],
  partially_paid: ['paid', 'written_off'],
  paid: [],
  written_off: [],
};

export function canTransitionDocument(from: DocumentStatus, to: DocumentStatus): boolean {
  if (from === to) return false;
  return VALID[from].includes(to);
}

export function assertDocumentTransition(from: DocumentStatus, to: DocumentStatus): void {
  if (!canTransitionDocument(from, to)) {
    throw new InvalidTransitionError('document', from, to);
  }
}

/**
 * After applying a new payment, return the resulting document status. Throws
 * if the payment overshoots the document total.
 */
export function computeStatusFromPayments(
  totalPaise: Paise,
  appliedPaiseAfter: Paise,
  currentStatus: DocumentStatus,
): DocumentStatus {
  if (appliedPaiseAfter < 0n) {
    throw new RuleViolationError('billing.negative_applied', 'Applied amount cannot be negative.');
  }
  if (appliedPaiseAfter > totalPaise) {
    throw new RuleViolationError(
      'billing.overpayment',
      `Applied amount ${appliedPaiseAfter} exceeds document total ${totalPaise}.`,
      { applied: appliedPaiseAfter.toString(), total: totalPaise.toString() },
    );
  }
  if (currentStatus === 'written_off' || currentStatus === 'draft') return currentStatus;
  if (appliedPaiseAfter === totalPaise) return 'paid';
  if (appliedPaiseAfter > 0n) return 'partially_paid';
  return currentStatus;
}

export interface ApplyPaymentResult {
  newAppliedSoFar: Paise;
  newStatus: DocumentStatus;
}

export function applyPayment(
  doc: { totalPaise: Paise; status: DocumentStatus },
  appliedSoFar: Paise,
  newAmountPaise: Paise,
): ApplyPaymentResult {
  if (newAmountPaise <= 0n) {
    throw new RuleViolationError('billing.non_positive_payment', 'Payment amount must be positive.');
  }
  if (doc.status === 'draft') {
    throw new RuleViolationError('billing.payment_on_draft', 'Cannot apply payment to a draft document.');
  }
  if (doc.status === 'written_off') {
    throw new RuleViolationError('billing.payment_on_written_off', 'Cannot apply payment to a written-off document.');
  }
  const after = paise(appliedSoFar + newAmountPaise);
  const newStatus = computeStatusFromPayments(doc.totalPaise, after, doc.status);
  return { newAppliedSoFar: after, newStatus };
}

// ----- AR aging -----

export type AgingBucket = 'current' | '0_30' | '31_60' | '61_90' | '90_plus';

/**
 * Given days-overdue (negative if not yet due), return the bucket.
 *   d ≤ 0        → current
 *   0 < d ≤ 30   → 0_30
 *   30 < d ≤ 60  → 31_60
 *   60 < d ≤ 90  → 61_90
 *   d > 90       → 90_plus
 */
export function agingBucket(daysOverdue: number): AgingBucket {
  if (!Number.isFinite(daysOverdue)) {
    throw new RuleViolationError('billing.non_finite_days', 'daysOverdue must be finite');
  }
  if (daysOverdue <= 0) return 'current';
  if (daysOverdue <= 30) return '0_30';
  if (daysOverdue <= 60) return '31_60';
  if (daysOverdue <= 90) return '61_90';
  return '90_plus';
}

// ----- Financial year math (India, April–March) — helper for billing -----

/** Given a YYYY-MM-DD, return the FY start year. April 2025 → 2025; March 2025 → 2024. */
export function fyStartYear(isoDate: string): number {
  const [y, m] = isoDate.split('-').map((n) => Number(n));
  if (!y || !m) throw new RuleViolationError('billing.bad_date', `bad date ${isoDate}`);
  return m >= 4 ? y : y - 1;
}
